import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Container } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import Box from "@mui/material/Box";
import HomeIcon from "@mui/icons-material/Home";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import LogoutIcon from "@mui/icons-material/Logout";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import ViewListIcon from "@mui/icons-material/ViewList";
import AggregateIcon from "@mui/icons-material/Analytics";
import ApprovalIcon from "@mui/icons-material/CheckCircle";
import QueueIcon from "@mui/icons-material/Queue";
import NewsletterIcon from "@mui/icons-material/Email";
import BroadcastIcon from "@mui/icons-material/Warning";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getRole } from "../../../helpers/auth";
import { roles } from "../../../constants/routes";
import { useOrganisation } from "../../../hooks/useOrganisation";
import "./style.css";
import { getTextColor, setDynamicFavicon } from "../../../helpers";
import { IMAGE_URL_AZURE } from "../../../constants/env";
import { clearAllFilters } from "../../../helpers/customEvents";
import ConsistentContainer from "../../../containers/ConsistentContainer";

const NavBar = () => {
  const navigate = useNavigate();
  const companyName = localStorage.getItem("companyName");
  const queryClient = useQueryClient();

  const { primaryColor, logoLink } = useOrganisation();
  const { pathname } = useLocation();
  const role = getRole();
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(false);

  // Ensure we have a fallback color if primaryColor is undefined
  const safeColor = getTextColor(primaryColor);
  const sidebarTextColor = getTextColor(primaryColor);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("companyName");
    localStorage.removeItem("organisationId");
    localStorage.removeItem("logoCompany");
    localStorage.clear();
    setDynamicFavicon(null);

    navigate("/");
    queryClient.refetchQueries({
      queryKey: ["onboardedOrganisation"],
    });
  };

  const handleMenuRoute = (route) => {
    navigate(route);
    handleMenuClose();
  };

  // New function to handle navigation and filter clearing
  const handleHomeNavigation = () => {
    if (roles.SuperAdmin === role) {
      navigate(`/workspace/superadmin-dashboard`);
    } else {
      navigate(`/${companyName}/dashboard`);

      // Dispatch custom event to clear filters
      const event = new CustomEvent(clearAllFilters);
      window.dispatchEvent(event);
    }
  };

  // Toggle admin sidebar
  const toggleAdminSidebar = () => {
    setAdminSidebarOpen(!adminSidebarOpen);
  };

  // Handle admin sidebar navigation
  const handleAdminNavigation = (route) => {
    navigate(route);
    setAdminSidebarOpen(false);
  };

  // Admin menu items based on role - separated into main and admin sections
  const getAdminMenuItems = () => {
    const baseItems = [];

    // Add Create content and View all content for non-SuperAdmin roles
    if (role !== roles.SuperAdmin) {
      baseItems.push(
        {
          text: "Create content",
          icon: <AddIcon />,
          route: `/${companyName}/create-content`,
        },
        {
          text: "View all content",
          icon: <ViewListIcon />,
          route: `/${companyName}/all-content`,
        }
      );
    }

    // Middle section items based on role
    const middleItems =
      roles.ContentCreator === role || role === roles.SuperAdmin
        ? []
        : roles.Editor === role
        ? [
            {
              text: "Aggregate",
              icon: <AggregateIcon />,
              route: `/${companyName}/Rss-Feed`,
            },
            {
              text: "Approve content",
              icon: <ApprovalIcon />,
              route: `/${companyName}/all-content/approve`,
            },
            {
              text: "Queued content",
              icon: <QueueIcon />,
              route: `/${companyName}/all-content/schedule`,
            },
            {
              text: "Newsletter content",
              icon: <NewsletterIcon />,
              route: `/${companyName}/all-newsletter`,
            },
          ]
        : [
            {
              text: "Aggregate",
              icon: <AggregateIcon />,
              route: `/${companyName}/Rss-Feed`,
            },
            {
              text: "Approve content",
              icon: <ApprovalIcon />,
              route: `/${companyName}/all-content/approve`,
            },
            {
              text: "Queued content",
              icon: <QueueIcon />,
              route: `/${companyName}/all-content/schedule`,
            },
            {
              text: "Newsletter content",
              icon: <NewsletterIcon />,
              route: `/${companyName}/all-newsletter`,
            },
            {
              text: "Broadcast to all",
              icon: <BroadcastIcon />,
              route: `/${companyName}/broadcast`,
            },
          ];

    const mainItems = [...baseItems, ...middleItems];

    // Last section items based on role (separated section)
    const adminItems =
      role === roles.SuperAdmin
        ? [
            {
              text: "Add/manage organisations",
              icon: <PeopleIcon />,
              route: `/${companyName}/all-organisation`,
            },
            {
              text: "Add/manage users",
              icon: <PeopleIcon />,
              route: `/${companyName}/all-users`,
            },
            {
              text: "Statistics",
              icon: <BarChartIcon />,
              route: `/${companyName}/statistics`,
            },
          ]
        : role === roles.Admin
        ? [
            {
              text: "Add/manage users",
              icon: <PeopleIcon />,
              route: `/${companyName}/all-users`,
            },
            {
              text: "Statistics",
              icon: <BarChartIcon />,
              route: `/${companyName}/statistics`,
            },
            {
              text: "Settings config",
              icon: <SettingsIcon />,
              route: `/${companyName}/settings/config`,
            },
          ]
        : [];

    return { mainItems, adminItems };
  };

  const { mainItems, adminItems } = getAdminMenuItems();

  // Updated styles with better visibility
  const iconButtonStyle = {
    cursor: "pointer",
    color: safeColor,
    "&:hover": {
      backgroundColor: "rgba(150, 247, 220, 0.08)", // Light hover effect
    },
  };

  const menuItemStyle = {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(150, 247, 220, 0.08)",
    },
  };

  // Render onboarding navbar
  if (pathname.includes(`/${companyName}/onboarding`.replaceAll(" ", "%20"))) {
    return (
      <Container maxWidth="lg" className="sectionCenter">
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar sx={{ justifyContent: "flex-end", padding: 0 }}>
            <Box sx={{ display: "flex" }}>
              <MenuItem onClick={handleLogout} sx={menuItemStyle}>
                <LogoutIcon fontSize="small" sx={{ mr: 1, color: safeColor }} />
                <Typography color={safeColor}>Log out</Typography>
              </MenuItem>
            </Box>
          </Toolbar>
        </AppBar>
      </Container>
    );
  }

  // Main navbar
  return (
    <>
      <div style={{ backgroundColor: primaryColor }}>
        <ConsistentContainer maxWidth="lg">
          <AppBar
            position="static"
            color="transparent"
            elevation={0}
            style={{
              paddingLeft: "0!important",
              paddingRight: "0!important",
            }}
          >
            <Toolbar
              sx={{
                justifyContent: "space-between",
                paddingLeft: "0!important",
                paddingRight: "0!important",
              }}
            >
              {/* Logo Section */}
              <Box
                onClick={handleHomeNavigation}
                component="img"
                src={
                  logoLink
                    ? `${logoLink}?${IMAGE_URL_AZURE}`
                    : "/favicon/Komz_Logo_GreenBlock.png"
                }
                alt={logoLink ? "Company Logo" : "Komz Logo"}
                sx={{
                  height: { xs: "40px", sm: "auto" },
                  maxHeight: { xs: "40px", sm: "100px" },
                  maxWidth: { xs: "120px", sm: "200px" },
                  width: "auto",
                  objectFit: "contain",
                  cursor: "pointer",
                  display: "block", // Ensure it's always visible
                }}
              />

              {/* Right side navigation */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Home Icon */}
                <IconButton onClick={handleHomeNavigation} sx={iconButtonStyle}>
                  <HomeIcon />
                </IconButton>

                {/* Settings Menu - Only for non-SuperAdmin */}
                {role !== roles.SuperAdmin && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      color="inherit"
                      onClick={handleMenuOpen}
                      sx={iconButtonStyle}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 1,
                          display: { xs: "none", sm: "block" },
                          color: safeColor,
                          fontWeight: "bold",
                        }}
                      >
                        Your Settings
                      </Typography>
                      <AccountCircleRoundedIcon sx={{ ml: 1 }} />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        elevation: 4,
                        sx: { width: 200 },
                      }}
                    >
                      <MenuItem
                        onClick={() =>
                          handleMenuRoute(`/${companyName}/settings/account`)
                        }
                        sx={menuItemStyle}
                      >
                        Account
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleMenuRoute(
                            `/${companyName}/settings/preferences`
                          )
                        }
                        sx={menuItemStyle}
                      >
                        Preference Center
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout} sx={menuItemStyle}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                        Log out
                      </MenuItem>
                    </Menu>
                  </Box>
                )}

                {/* Admin Button - For non-Reader roles */}
                {role !== roles.Reader && role !== roles.SuperAdmin && (
                  <IconButton
                    onClick={toggleAdminSidebar}
                    sx={{
                      ...iconButtonStyle,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <AdminPanelSettingsIcon
                      sx={{
                        display: { xs: "block", sm: "none" },
                      }}
                    />
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        ml: { xs: 0, sm: 1 },
                        letterSpacing: ".2rem",
                        textDecoration: "none",
                        display: { xs: "none", sm: "block" },
                        fontWeight: "bold",
                        color: safeColor,
                      }}
                    >
                      Admin
                    </Typography>
                    {/* Show up caret when sidebar is open, down caret when closed */}
                    {adminSidebarOpen ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          ml: 0.5,
                          fontSize: "1.2rem",
                          color: safeColor,
                        }}
                      />
                    ) : (
                      <KeyboardArrowDownIcon
                        sx={{
                          ml: 0.5,
                          fontSize: "1.2rem",
                          color: safeColor,
                        }}
                      />
                    )}
                  </IconButton>
                )}

                {/* SuperAdmin Logout */}
                {role === roles.SuperAdmin && (
                  <IconButton onClick={handleLogout} sx={iconButtonStyle}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        color: safeColor,
                        fontWeight: "bold",
                      }}
                    >
                      Log out
                    </Typography>
                  </IconButton>
                )}
              </Box>
            </Toolbar>
          </AppBar>
        </ConsistentContainer>
      </div>

      {/* Admin Sidebar */}
      <Drawer
        anchor="right"
        open={adminSidebarOpen}
        onClose={() => setAdminSidebarOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            backgroundColor: primaryColor,
            color: sidebarTextColor,
          },
        }}
      >
        {/* Sidebar Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: `1px solid ${
              sidebarTextColor === "#ffffff"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)"
            }`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: sidebarTextColor,
              fontWeight: "bold",
            }}
          >
            Admin
          </Typography>
          <IconButton
            onClick={() => setAdminSidebarOpen(false)}
            sx={{ color: sidebarTextColor }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Sidebar Menu Items */}
        <List sx={{ flex: 1, pt: 1 }}>
          {/* Main Content Management Items */}
          {mainItems.length > 0 && (
            <>
              {mainItems.map((item, index) => (
                <ListItem key={`main-${index}`} disablePadding>
                  <ListItemButton
                    onClick={() => handleAdminNavigation(item.route)}
                    sx={{
                      color: `${sidebarTextColor}B3`, // 70% opacity (B3 = 179/255 ≈ 0.7)
                      "&:hover": {
                        backgroundColor:
                          sidebarTextColor === "#ffffff"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                        color: sidebarTextColor, // 100% opacity on hover
                        "& .MuiListItemIcon-root": {
                          color: sidebarTextColor, // Icon 100% opacity on hover
                        },
                        "& .MuiListItemText-primary": {
                          color: sidebarTextColor, // Text 100% opacity on hover
                        },
                      },
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: `${sidebarTextColor}B3`, // 70% opacity by default
                        minWidth: 40,
                        transition: "color 0.2s ease-in-out",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        color: `${sidebarTextColor}B3`, // 70% opacity by default
                        transition: "color 0.2s ease-in-out",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {/* Divider between main items and admin items */}
              {adminItems.length > 0 && (
                <Divider
                  sx={{
                    my: 1,
                    backgroundColor: getTextColor(primaryColor),
                    mx: 2,
                  }}
                />
              )}
            </>
          )}

          {/* Admin Management Items (Users, Statistics, Settings) */}
          {adminItems.length > 0 ? (
            adminItems.map((item, index) => (
              <ListItem key={`admin-${index}`} disablePadding>
                <ListItemButton
                  onClick={() => handleAdminNavigation(item.route)}
                  sx={{
                    color: `${sidebarTextColor}B3`, // 70% opacity by default
                    "&:hover": {
                      backgroundColor:
                        sidebarTextColor === "#ffffff"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)",
                      color: sidebarTextColor, // 100% opacity on hover
                      "& .MuiListItemIcon-root": {
                        color: sidebarTextColor, // Icon 100% opacity on hover
                      },
                      "& .MuiListItemText-primary": {
                        color: sidebarTextColor, // Text 100% opacity on hover
                      },
                    },
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: `${sidebarTextColor}B3`, // 70% opacity by default
                      minWidth: 40,
                      transition: "color 0.2s ease-in-out",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: `${sidebarTextColor}B3`, // 70% opacity by default
                      transition: "color 0.2s ease-in-out",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          ) : mainItems.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No admin options available"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  color: sidebarTextColor,
                  textAlign: "center",
                }}
              />
            </ListItem>
          ) : null}
        </List>
      </Drawer>
    </>
  );
};

export default NavBar;
